// src/App.jsx
/**
 * Application entry point.
 *
 * Responsibilities
 * ────────────────
 *  • Mount the two possible nav variants (`<NavBar/> | <MDNavBar/>`).
 *  • Gate protected routes behind `<ProtectedRoute/>`.
 *  • Lazy-load page modules to keep initial bundle size low.
 *  • Redirect unmatched paths to `/`.
 *  • Run a quick device-capability check (`useDeviceCompatibility`). On
 *    unsupported devices we replace the `/auth/*` subtree with a
 *    compatibility-warning page.
 *
 * Layout skeleton
 *  <div class="h-[100dvh] flex flex-col">   ← fills viewport height
 *    {NavBar}
 *    <Suspense>  ← lazy pages rendered inside
 *       <Routes> … </Routes>
 *    </Suspense>
 *  </div>
 *
 * Notes
 *  • Use `100dvh` (dynamic viewport unit) so Safari iOS 15+ doesn’t crop
 *    the content when the URL bar shrinks.
 *  • `typography` Tailwind plugin class gives consistent heading / paragraph
 *    defaults across pages.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { firebaseApp } from "./firebase/firebaseApp";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import AccessDenied from "./components/authentication/AccessDenied";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";

import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import LegalDocumentPage from "./components/legal/LegalDocumentPage";
import LegalUpdateOverlay from "./components/legal/LegalUpdateOverlay";

import { useAuth } from "./context/AuthContext";
import { usePresentationLabelling } from "./hooks/usePresentationLabelling";
import {
  canManageDashboardConfiguration,
  canManageDashboardOrganisation,
  canViewDashboardPatientData,
  canViewDashboardStudies,
} from "./utils/authentication/dashboardPermissions";
import PatientProfilePage from "./pages/PatientProfilePage";
import AdminPage from "./pages/AdminPage";
import AboutPage from "./pages/AboutPage";
import {
  clearStoredSelectedPatient,
  readStoredSelectedPatient,
  writeStoredSelectedPatient,
} from "./utils/patientProfileSelection";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
const StudiesPage = lazy(() => import("./pages/StudiesPage"));
const NewStudyPage = lazy(() => import("./pages/NewStudyPage"));
const StudySettingsPage = lazy(() => import("./pages/StudySettingsPage"));
/* ------------------------------------------------------------------ */

export default function App() {
  const { user, claims, accountContext, organisationLoading, isBootstrapAdmin } =
    useAuth(); // grab host’s user
  const { appName } = usePresentationLabelling("dashboard");
  const navigate = useNavigate();
  const handleLegalExit = () => {
    signOut(getAuth(firebaseApp)).finally(() =>
      navigate("/auth/login", { replace: true }),
    );
  };
  const [userData] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(() =>
    readStoredSelectedPatient(),
  );

  // Admin-only routes use the centralised predicate (backend role/membership,
  // with the interim Firebase-claims bridge) — see utils/authentication.
  const permissionBridge = {
    claims,
    email: user?.email,
    isBootstrapAdmin,
  };
  const canManageOrganisation = canManageDashboardOrganisation(
    accountContext,
    permissionBridge,
  );
  const canViewPatients = canViewDashboardPatientData(
    accountContext,
    permissionBridge,
  );
  const canViewStudies = canViewDashboardStudies(
    accountContext,
    permissionBridge,
  );
  const canManageConfiguration = canManageDashboardConfiguration(
    accountContext,
    permissionBridge,
  );
  const fallbackProtectedPath = canViewPatients
    ? "/patients"
    : canViewStudies
      ? "/studies"
      : canManageOrganisation
        ? "/admin"
        : null;
  const fallbackProtectedRouteElement = fallbackProtectedPath ? (
    <Navigate to={fallbackProtectedPath} replace />
  ) : (
    <AccessDenied reason="denied" />
  );

  const renderPermissionGatedPage = (canAccess, page) => {
    if (organisationLoading && !canAccess) {
      return <div className="h-full w-full flex items-center justify-center">Loading…</div>;
    }

    return canAccess ? page : fallbackProtectedRouteElement;
  };

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  useEffect(() => {
    if (typeof document === "undefined" || !appName) {
      return;
    }

    document.title = appName.endsWith("Dashboard")
      ? appName
      : `${appName} Dashboard`;
  }, [appName]);

  useEffect(() => {
    if (selectedPatient) {
      writeStoredSelectedPatient(selectedPatient);
      return;
    }

    clearStoredSelectedPatient();
  }, [selectedPatient]);

  useEffect(() => {
    if (user) {
      return;
    }

    setSelectedPatient(null);
    clearStoredSelectedPatient();
  }, [user]);

  // While a signed-in user's claims are still resolving, show a loading screen
  // so admin-gated routes don't briefly evaluate against undefined claims.
  if (user && claims === null) {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col typography relative text-typography">
      {navVariant === "main" ? <NavBar /> : <MDNavBar />}

      <LegalUpdateOverlay onExit={handleLegalExit} />

      {/* Suspense boundary – shows fallback while lazy chunks download */}
      <div className="h-full min-h-0 flex md:items-start md:justify-center ">
        <Suspense fallback={<div className="m-auto">Loading…</div>}>
          <Routes>
            {/* ---------- Public routes ---------- */}
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/auth/*"
              // element={<AuthPage />}
              element={<AuthPage />}
            />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordRequest />}
            />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route
              path="/legal/terms"
              element={<LegalDocumentPage documentType="terms" />}
            />
            <Route
              path="/legal/privacy"
              element={<LegalDocumentPage documentType="privacy" />}
            />
            <Route path="/about" element={<AboutPage />} />
            {/* ---------- Protected routes ---------- */}
            <>
              <Route
                path="/patients"
                element={
                  <ProtectedRoute>
                    {renderPermissionGatedPage(
                      canViewPatients,
                      <PatientsPage
                        userData={userData}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                      />,
                    )}
                  </ProtectedRoute>
                }
              />
            </>
            <>
              <Route
                path="/patients/profile"
                element={
                  <ProtectedRoute>
                    {renderPermissionGatedPage(
                      canViewPatients,
                      <PatientProfilePage
                        userData={userData}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                      />,
                    )}
                  </ProtectedRoute>
                }
              />
            </>

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    {renderPermissionGatedPage(
                      canManageOrganisation,
                      <AdminPage />,
                    )}
                  </ProtectedRoute>
                }
              />

            <Route
              path="/studies"
              element={
                <ProtectedRoute>
                  {renderPermissionGatedPage(
                    canViewStudies,
                    <StudiesPage />,
                  )}
                </ProtectedRoute>
              }
            />

            <Route
              path="/studies/new"
              element={
                <ProtectedRoute>
                  {renderPermissionGatedPage(
                    canManageConfiguration,
                    <NewStudyPage />,
                  )}
                </ProtectedRoute>
              }
            />

            <Route
              path="/studies/:studyCode/settings"
              element={
                <ProtectedRoute>
                  {renderPermissionGatedPage(
                    canManageConfiguration,
                    <StudySettingsPage />,
                  )}
                </ProtectedRoute>
              }
            />
            {/* ---------- Fallback ---------- */}
            <Route
              path="*"
              element={
                <Navigate to={fallbackProtectedPath || "/patients"} replace />
              }
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
